/**
 * SayItBack scoring: record a short snippet (tap to start, tap to stop, 6 s
 * cap) and have the server score it against the expected line. The audio is
 * scored then discarded — never stored. Throws on mic denial or scoring
 * failure so the caller can fall back to self-marking ("I said it").
 */

const MAX_SEC = 6;

export async function recordSnippet(
  onLevel: (level: number) => void,
  stopSignal: { stop: boolean },
): Promise<Blob> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
  });
  let ctx: AudioContext | undefined;
  try {
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    const done = new Promise<Blob>((resolve) => {
      rec.onstop = () => resolve(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
    });
    // Level meter so the learner sees the mic is live.
    ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    const buf = new Uint8Array(an.fftSize);
    rec.start();
    const t0 = performance.now();
    await new Promise<void>((resolve) => {
      const tick = () => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        onLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
        if (stopSignal.stop || performance.now() - t0 > MAX_SEC * 1000) resolve();
        else requestAnimationFrame(tick);
      };
      tick();
    });
    rec.stop();
    // Await inside try: the finally below stops the mic tracks, which must
    // not happen while MediaRecorder is still finalising (lost tail/empty blob).
    return await done;
  } finally {
    stream.getTracks().forEach((t) => t.stop());
    void ctx?.close().catch(() => {});
  }
}
